<template>
  <div :class="controlClasses" class="mds-form-control mds-input-text">
    <label v-if="label" :for="uid" :style="labelStyles">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :id="innerId" :style="innerStyles" class="inner">
      <div class="control-row">
        <div class="mds-form-control-icon" v-if="hasIconSlot">
          <slot name="icon" />
        </div>

        <template v-if="isDisabledHintVisible">
          <button class="disable-hint-container" :id="`hint-${uid}`">
            <VIcon theme="outlined" material="question_mark" />
          </button>
          <VTooltip :target="`#hint-${uid}`">
            <ul>
              <li v-for="(reason, index) in disabledReasons" :key="index">
                {{ reason }}
              </li>
            </ul>
          </VTooltip>
        </template>


        <button v-if="showClear" @click="onClear" class="clear" type="button">
          <VIcon material="cancel" />
        </button>

        <input
          :id="uid"
          :disabled="disabled"
          :placeholder="placeholder"
          :type="type"
          :value="currentValue"
          :autocomplete="autocomplete"
          ref="nativeInput"
          @input="onInput"
          @blur="onBlur"
          @focus="onFocus"
        />

        <span class="unit" v-if="unit">{{ unit }}</span>
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li v-for="(error, index) in validation.errors" :key="'error' + index">
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li v-for="(warning, index) in validation.warnings" :key="'warning' + index">
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <slot name="helper-text" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./VInputV2.scss"/>

<script lang="ts">
import Constants from "@/core/misc/Constants.js";
import VBaseControl from "@/core/services/VBaseControl";

const INPUT_TYPES = ["text", "tel", "email", "password", "search", "url"];

export default defineComponent({
  name: "VInputV2",

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.
    // Здесь только специфика VInput.

    /** v-model */
    modelValue: { type: [String, Number, undefined], default: null },
    /** Обратная совместимость с :value */
    value:      { type: [String, Number], default: null },
    /** Кнопка очистки */
    clearable:  { type: Boolean,          default: false },
    /** Тип инпута */
    type: {
      type:      String,
      default:   "text",
      validator: (value: string) => INPUT_TYPES.includes(value),
    },
    /** Выравнивание текста */
    textAlign: {
      type:      String,
      default:   null,
      validator: (value: string) => Constants.TEXT_ALIGNS.includes(value),
    },
    /** Autocomplete */
    autocomplete: { type: String, default: null },
    /** Единица измерения, отображается справа от инпута */
    unit: { type: String, default: null },
    /** Реактивный источник для инициализации значения, напр. () => localPosition.product */
    dependsOn: { type: Function, default: null },
    /** Извлекает значение из источника, напр. (product) => product.code */
    default:   { type: Function, default: null },
  },

  emits: ["update:modelValue", "input", "blur", "focus"],

  computed: {
    /** Приоритет modelValue, потом value (обратная совместимость). */
    currentValue() {
      if (this.modelValue !== null) return this.modelValue;
      return this.value;
    },

    innerId() {
      return `inner-${this.uid}`;
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true) classes.push("clearable");
      return classes;
    },

    /** labelStyles переопределяем — у инпута исторически width, а не flex. */
    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) {
        result["width"] = `calc(100% - ${this.labelWidth})`;
      }
      if (this.textAlign !== null) {
        result["text-align"] = this.textAlign;
      }
      return result;
    },

    showClear(): boolean {
      return this.clearable === true
        && this.currentValue !== null
        && this.currentValue !== ""
        && this.disabled === false;
    },

    hasIconSlot(): boolean {
      return this.$slots.icon !== undefined;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },
  },

  watch: {
    currentValue(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.debounceValidation();
      }
    },
  },

  created() {
    if (this.dependsOn !== null && this.default !== null) {
      this.$watch(this.dependsOn, (source) => {
        if (source) {
          this.$emit("update:modelValue", this.default(source));
        }
      });
    }
  },

  methods: {
    onInput(event) {
      const value = event.target.value;
      if (this.validation.isDirty === false && this.currentValue !== value) {
        this.validation.markDirty();
      }
      this.$emit("update:modelValue", value);
      this.$emit("input", value);
    },

    async onBlur(event) {
      if (typeof this.debounceValidation.cancel === "function") {
        this.debounceValidation.cancel();
      }
      await this.validate();
      this.$emit("blur", event.target.value);
    },

    onFocus(event) {
      this.validation.markTouched();
      this.$emit("focus", event.target);
    },

    onClear() {
      this.$emit("update:modelValue", null);
      this.$emit("input", null);
      this.$emit("blur", null);
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(this.currentValue);
    },

    focus() {
      const input = this.$refs.nativeInput;
      if (input !== undefined) {
        input.focus();
      }
    },
  },
});
</script>
